import { Component, OnInit, OnChanges, Input, Output, forwardRef, EventEmitter } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, Validator, NG_VALIDATORS, AbstractControl, ValidationErrors } from '@angular/forms';
import { UserProfileService } from '../../../api/user-profile.service';

@Component({
  selector: 'iv-notes-field',
  templateUrl: './notes-field.component.html',
  styleUrls: ['./notes-field.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => NotesFieldComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => NotesFieldComponent),
      multi: true
    }
  ]
})
export class NotesFieldComponent implements OnInit, OnChanges, ControlValueAccessor, Validator {
  fieldStyles = { width: '100%'};
  commonDenominator = 254;
  caretPosition = 0;
  private isTouched = false;
  @Input() customStyles: {}; // { background: '#somecolor', font-size: 'somesize' }
  @Input() controlName = '';
  @Input() identifier = '';
  @Input() placeHolder = '';
  @Input() disabled = false;
  @Input() maxLength = '';
  @Input() headerNote = '';
  @Input() subheaderNote = '';
  @Input() amount: number;
  @Input() currencySymbol = '';
  @Input() displayFooter = false;
  // tslint:disable-next-line: no-input-rename
  @Input('fieldValue') _value = '';
  @Input() displayLines = false;
  // For Invoice Details page
  @Input() onInvoiceDetails: Boolean = false;
  @Output() removeNoteEmmiter = new EventEmitter();
  @Output() addNoteEmmiter = new EventEmitter();

  constructor(private userProfileService: UserProfileService) { }

  get fieldValue() {
    return this._value;
  }

  set fieldValue(val) {
    if (val) {
      this._value = val;
      this.propagateChange(val);
    }
  }

  // the method set in registerOnChange to emit changes back to the form
  propagateChange = (_: any) => { };
  propagateTouched = (_: any) => { };

  ngOnInit() {
    // Getting new styles, if any
    Object.assign(this.fieldStyles, this.customStyles);
  }

  ngOnChanges(changes) {
    this.propagateChange(this.fieldValue);
  }

  writeValue(value) {
    this.fieldValue = value;
    // Reset touched state when value is set programmatically
    if (value === '' || value === null || value === undefined) {
      this.isTouched = false;
    }
  }

  /**
   * Write form disabled state to the DOM element (model => view)
   */
  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  // registers 'fn' that will be fired when changes are made
  // this is how we emit the changes back to the form
  registerOnChange(fn) {
    this.propagateChange = fn;
  }

  // not used, used for touch input
  registerOnTouched(fn) {
    this.propagateTouched = fn;
  }

  // change events from the textarea
  onChange(event) {
    // get value from textarea
    const newValue = event.target.value;
    this._value = newValue;
    this.propagateChange(this.fieldValue);
    if (!this.isTouched) {
      this.isTouched = true;
      this.propagateTouched(true);
    }
  }
  onBlur() {
    if (!this.isTouched) {
      this.isTouched = true;
      this.propagateTouched(true);
    }
  }

  getLines() {
    return this.fieldValue.length === 0 ? 1 :
      this.fieldValue.length < this.commonDenominator ? 1 : Math.ceil(this.fieldValue.length / this.commonDenominator);
  }

  getRemainingCharacters() {
    return (this.commonDenominator * this.getLines()) - this.fieldValue.length;
  }

  getReminder() {
    let reminder = '';
    const usedCharacters = this.commonDenominator + (this.fieldValue.length - this.commonDenominator * this.getLines());
    if (this.getRemainingCharacters() <= 7) {
      reminder = `You're using ${usedCharacters}/${this.commonDenominator} characters. Continued Input will start on a new line after 254!`;
    } else {
      reminder = `Remaining characters: ${this.getRemainingCharacters()}`;
    }
    return reminder;
  }

  getCaretPosition(textarea) {
    if (textarea.selectionStart || textarea.selectionStart === '0') {
      this.caretPosition = textarea.selectionStart;
    }
  }

  // Add a special character to use it as a break point E.g. \n
  addNewLine() {
    const text = this.fieldValue.replace(/\n/g, '');
    const arrayLines: string[] = [];
    if (text.length > this.commonDenominator) {
      const lines = this.getLines();
      for (let i = 0; i < lines; i++) {
        const start = i * this.commonDenominator; // 0, 254, 508, etc...
        arrayLines.push(text.substring(start, this.commonDenominator));
      }
    }
    this.fieldValue = arrayLines.join('\n');
  }

  removeNote() {
    this._value = '';
    this.removeNoteEmmiter.emit(true);
  }

  addNote() {
    this._value = ' ';
    this.addNoteEmmiter.emit(true);
  }

  isIndiaUser(): boolean {
    return this.userProfileService.getCountryLocalStorage() === 'IND';
  }
  isFieldRequired(): boolean {
    return this.isIndiaUser();
  }
  isMinLengthValid(): boolean {
    if (!this.isIndiaUser()) {
      return true;
    }
    return this.fieldValue.trim().length >= 25;
  }
  getValidationError(): string {
    if (this.isIndiaUser()) {
      if (this.fieldValue.trim().length < 25) {
        return `Minimum 25 characters required.`;
      }
    }
    return '';
  }
  hasValidationError(): boolean {
    return this.isTouched && this.isIndiaUser() && (this.fieldValue.trim().length === 0 || this.fieldValue.trim().length < 25);
  }
  validate(control: AbstractControl): ValidationErrors | null {
    if (this.isIndiaUser()) {
      const value = control.value || '';
      if (value.trim().length === 0) {
        return { required: true };
      }
      if (value.trim().length < 25) {
        return { minLength: { requiredLength: 25, actualLength: value.trim().length } };
      }
    }
    return null;
  }

}
